// @ts-nocheck
// ============================================
// App Slice - управление приложениями, версиями и экранами
// ============================================

import { StateCreator } from 'zustand';
import { nanoid } from 'nanoid';
import { App, AppVersion, Screen } from '../../types';
import { isFirebaseAvailable, createProject, createScreen as firebaseCreateScreen, addVersion as firebaseAddVersion } from '../../lib/firebaseService';
import { updateScreen as firebaseUpdateScreen, deleteVersion as firebaseDeleteVersion, deleteScreen as firebaseDeleteScreen, updateVersion as firebaseUpdateVersion, updateProject as firebaseUpdateProject, getProject } from '../../lib/firebase/projectService';
import { uploadScreenImage as uploadToCloudinary, isCloudinaryAvailable } from '../../lib/cloudinaryService';
import { saveAppId, saveVersionId, saveScreenId, clearNavigationState } from '../../utils/navigationState';

// Generate unique IDs using nanoid
const generateId = () => nanoid();

export interface AppState {
  // Apps state
  apps: App[];
  currentApp: App | null;
  currentAppId: string | null;
  
  // Versions state
  currentVersion: AppVersion | null;
  currentVersionId: string | null;
  
  // Screens state
  currentScreen: Screen | null;
  currentScreenId: string | null;
  
  // Apps actions
  setCurrentApp: (appId: string) => void;
  addApp: (name: string, description: string) => Promise<string | null>;
  updateApp: (appId: string, updates: Partial<Pick<App, 'name' | 'description'>>) => void;
  deleteApp: (appId: string) => Promise<void>;
  
  // Versions actions
  setCurrentVersion: (versionId: string) => void;
  addVersion: (appId: string, version: string, name: string, copyScreens?: boolean) => Promise<string | null>;
  updateVersion: (versionId: string, updates: Partial<Pick<AppVersion, 'version' | 'name'>>) => void;
  deleteVersion: (versionId: string) => void;
  
  // Screens actions
  setCurrentScreen: (screenId: string) => void;
  addScreen: (screenData: { name: string; displayName: string; imageUrl?: string }) => Promise<string | null>;
  uploadScreenImage: (file: File) => Promise<string | null>;
  updateScreen: (screenId: string, updates: Partial<Pick<Screen, 'name' | 'displayName' | 'imageUrl' | 'markerScale' | 'popupScale'>>) => void;
  deleteScreen: (screenId: string) => void;
  reorderScreens: (screenIds: string[]) => void;
  moveScreen: (screenId: string, direction: 'up' | 'down') => void;
  updateScreenMarkerScale: (screenId: string, scale: number) => Promise<void>;
  updateScreenPopupScale: (screenId: string, scale: number) => Promise<void>;
}

export const createAppSlice: StateCreator<AppState, [], [], AppState> = (set, get) => ({
  // Initial state
  apps: [],
  currentApp: null,
  currentAppId: null,
  currentVersion: null,
  currentVersionId: null,
  currentScreen: null,
  currentScreenId: null,
  
  // Set current app
  setCurrentApp: (appId: string) => {
    const app = get().apps.find(a => a.id === appId);
    if (app) {
      const versions = app.versions || (app as any).tasks?.[0]?.versions || [];
      const activeVersion = versions.find((v: AppVersion) => v.isActive) || versions[0];
      const newState = { 
        currentApp: app, 
        currentAppId: app.id,
        currentVersion: activeVersion || null,
        currentVersionId: activeVersion?.id || null,
        currentScreen: activeVersion?.screens[0] || null,
        currentScreenId: activeVersion?.screens[0]?.id || null
      };
      set(newState);
      // Persist to localStorage using centralized utility
      saveAppId(app.id);
      if (activeVersion?.id) saveVersionId(activeVersion.id);
      if (activeVersion?.screens[0]?.id) saveScreenId(activeVersion.screens[0].id);
      
      // Sync history for the selected app
      const { syncHistoryWithFirebase, syncVersionsWithFirebase, syncScreensWithFirebase, syncEventsWithFirebase } = get() as any;
      if (syncHistoryWithFirebase) {
        syncHistoryWithFirebase(app.id);
      }
      // Start real-time sync for versions, screens, and events
      if (syncVersionsWithFirebase) {
        syncVersionsWithFirebase(app.id);
      }
      if (syncScreensWithFirebase) {
        syncScreensWithFirebase(app.id);
      }
      if (syncEventsWithFirebase) {
        syncEventsWithFirebase(app.id);
      }
    } else {
      set({ 
        currentApp: null, 
        currentAppId: null,
        currentVersion: null,
        currentVersionId: null,
        currentScreen: null,
        currentScreenId: null,
        selectedEvent: null
      });
      // Clear localStorage using centralized utility
      clearNavigationState();
    }
  },
  
  // Add new app
  addApp: async (name: string, description: string) => {
    const state = get() as any;
    const user = state.user;
    const { apps } = get();
    
    // DEBUG: Log full state to understand user structure
    console.log('[addApp] Debug - Full state keys:', Object.keys(state));
    console.log('[addApp] Debug - User from state:', user);
    console.log('[addApp] Debug - authSlice state:', {
      user: state.user,
      sessionExpiry: state.sessionExpiry
    });
    
    // DEBUG: Log initial state
    console.log('[addApp] Debug - Initial:', {
      name,
      description,
      hasUser: !!user,
      userRole: user?.role,
      userId: user?.id,
      userObject: user,
      firebaseAvailable: isFirebaseAvailable(),
      currentAppsCount: apps.length
    });
    
    try {
      // Always try to save to Firebase (remove DEV check for proper persistence)
      if (isFirebaseAvailable() && user) {
        console.log('[addApp] Creating project in Firebase with user:', { id: user.id, role: user.role });
        const newApp = await createProject({ name, description }, user);
        
        console.log('[addApp] createProject returned:', newApp);
        
        if (newApp) {
          set(state => ({ apps: [...state.apps, newApp] }));
          console.log('[addApp] ✅ App added successfully to local state, id:', newApp.id, 'total apps:', get().apps.length);
          return newApp.id;
        }
        console.log('[addApp] createProject returned null, falling back to local creation');
      } else {
        console.log('[addApp] Firebase not available or no user:', {
          firebaseAvailable: isFirebaseAvailable(),
          hasUser: !!user
        });
      }
      
      const now = new Date().toISOString();
      const newApp: App = {
        id: generateId(),
        name,
        description,
        createdAt: now,
        updatedAt: now,
        versions: [],
        ownerId: user?.id,
        members: user ? { [user.id]: { role: 'developer', status: 'active', addedAt: now } } : {}
      };
      set(state => ({ apps: [...state.apps, newApp] }));
      return newApp.id;
    } catch (error) {
      console.error('[addApp] Error creating app:', error);
      const now = new Date().toISOString();
      const newApp: App = {
        id: generateId(),
        name,
        description,
        createdAt: now,
        updatedAt: now,
        versions: [],
        ownerId: user?.id,
        members: user ? { [user.id]: { role: 'developer', status: 'active', addedAt: now } } : {}
      };
      set(state => ({ apps: [...state.apps, newApp] }));
      return newApp.id;
    }
  },
  
  // Update app
  updateApp: async (appId: string, updates: Partial<Pick<App, 'name' | 'description'>>) => {
    const { apps, currentApp, user } = get() as any;
    
    // Find old app for history
    const oldApp = apps.find((a: App) => a.id === appId);
    
    const updatedApps = apps.map(app => {
      if (app.id === appId) {
        return { ...app, ...updates, updatedAt: new Date().toISOString() };
      }
      return app;
    });
    
    set({ apps: updatedApps });
    
    if (currentApp?.id === appId) {
      set({ currentApp: { ...currentApp, ...updates } });
    }
    
    // Sync to Firebase if available
    if (isFirebaseAvailable() && user && currentApp?.id === appId) {
      try {
        await firebaseUpdateProject(
          appId,
          updates,
          user,
          oldApp
        );
        
        // Update timestamp AFTER successful Firebase operation
        const { updateLastLocalUpdate } = (get() as any);
        if (updateLastLocalUpdate) updateLastLocalUpdate();
        
        console.log('[updateApp] Saved to Firebase:', { appId, updates });
      } catch (error) {
        console.error('[updateApp] Error saving to Firebase:', error);
      }
    }
  },
  
  // Delete app
  deleteApp: async (appId: string) => {
    const { apps, currentApp, user } = get() as any;
    
    // Delete from Firebase first
    if (isFirebaseAvailable() && user) {
      const { deleteProject } = await import('../../lib/firebase/projectService');
      const success = await deleteProject(appId, user);
      
      // Update timestamp AFTER successful Firebase operation
      const { updateLastLocalUpdate } = (get() as any);
      if (updateLastLocalUpdate) updateLastLocalUpdate();
      
      if (!success) {
        console.error('[deleteApp] Failed to delete app from Firebase');
        return;
      }
    }
    
    const updatedApps = apps.filter(app => app.id !== appId);
    
    const newCurrentApp = currentApp?.id === appId
      ? (updatedApps.length > 0 ? updatedApps[0] : null)
      : currentApp;
    
    set({
      apps: updatedApps,
      currentApp: newCurrentApp,
      currentAppId: newCurrentApp?.id || null,
      currentVersion: newCurrentApp?.versions[0] || null,
      currentVersionId: newCurrentApp?.versions[0]?.id || null,
      currentScreen: newCurrentApp?.versions[0]?.screens[0] || null,
      currentScreenId: newCurrentApp?.versions[0]?.screens[0]?.id || null
    });
  },
  
  // Set current version
  setCurrentVersion: (versionId: string) => {
    const { currentApp } = get();
    if (!currentApp) return;
    
    const version = currentApp.versions.find(v => v.id === versionId);
    if (version) {
      set({ 
        currentVersion: version,
        currentVersionId: version.id,
        currentScreen: version.screens[0] || null,
        currentScreenId: version.screens[0]?.id || null
      });
      // Persist to localStorage using centralized utility
      saveVersionId(version.id);
      if (version.screens[0]?.id) saveScreenId(version.screens[0].id);
      
      // Sync screens and events for the new version
      const { syncScreensWithFirebase, syncEventsWithFirebase } = get() as any;
      if (syncScreensWithFirebase) {
        syncScreensWithFirebase(currentApp.id);
      }
      if (syncEventsWithFirebase) {
        syncEventsWithFirebase(currentApp.id);
      }
    } else {
      set({ 
        currentVersion: null,
        currentVersionId: null,
        currentScreen: null,
        currentScreenId: null
      });
      // Clear localStorage using centralized utility
      saveVersionId(null);
      saveScreenId(null);
    }
  },
  
  // Add new version
  addVersion: async (appId: string, version: string, name: string, copyScreens: boolean = false) => {
    const { apps, currentApp, currentVersion, user } = get() as any;
    
    const appIndex = apps.findIndex((a: App) => a.id === appId);
    if (appIndex === -1 || !currentApp) return null;
    
    // Сохраняем предыдущее состояние для rollback
    const previousApps = apps;
    const previousCurrentApp = currentApp;
    const previousCurrentVersion = currentVersion;
    const previousCurrentScreen = get().currentScreen;
    
    const screensToCopy = copyScreens && currentVersion ? currentVersion.screens : [];
    
    const copiedScreens = screensToCopy.map((screen: Screen) => ({
      ...screen,
      id: generateId(),
      events: screen.events.map((event: any) => ({
        ...event,
        id: generateId(),
        status: event.status || 'new'
      }))
    }));
    
    // Используем результат из Firebase вместо создания локальной версии
    let newVersion: AppVersion | null = null;
    
    // Update timestamp AFTER successful Firebase sync
    if (isFirebaseAvailable() && user && currentApp) {
      try {
        const result = await firebaseAddVersion(currentApp.id, version, name, user, copyScreens);
        
        // Update timestamp AFTER successful Firebase operation
        const { updateLastLocalUpdate } = (get() as any);
        if (updateLastLocalUpdate) updateLastLocalUpdate();
        
        if (!result?.data) {
          // Rollback on Firebase error
          console.log('[addVersion] ❌ Rollback - Firebase error');
          set({
            apps: previousApps,
            currentApp: previousCurrentApp,
            currentVersion: previousCurrentVersion,
            currentVersionId: previousCurrentVersion?.id || null,
            currentScreen: previousCurrentScreen,
            currentScreenId: previousCurrentScreen?.id || null
          });
          return null;
        }
        
        // Используем версию из Firebase - она уже содержит скопированные экраны (если copyScreens=true)
        // Cloud Function сама копирует экраны, поэтому не перезаписываем их
        newVersion = result.data;
        
        // DEBUG: Логируем что получили от Firebase
        console.log('[addVersion] 📋 Firebase response:', {
          hasData: !!result.data,
          screensCount: result.data?.screens?.length || 0,
          copyScreens
        });
        
        // Если это было копирование экранов - получаем актуальные данные из Firebase
        // Это важно, т.к. Cloud Function не возвращает данные версии с экранами
        if (copyScreens && currentApp) {
          console.log('[addVersion] 🔄 Refreshing project data from Firebase after copy...');
          const freshAppData = await getProject(currentApp.id);
          
          if (freshAppData && freshAppData.versions && freshAppData.versions.length > 0) {
            // Находим только что созданную версию (первая в списке - она активная)
            const newVer = freshAppData.versions[0];
            console.log('[addVersion] ✅ Fresh version data:', {
              versionId: newVer.id,
              screensCount: newVer.screens?.length || 0,
              eventsCount: newVer.screens?.reduce((acc, s) => acc + (s.events?.length || 0), 0) || 0
            });
            
            // Обновляем все данные приложения в store
            const updatedApps = apps.map((app: App) => {
              if (app.id === appId) {
                return {
                  ...freshAppData,
                  id: app.id // сохраняем оригинальный ID
                };
              }
              return app;
            });
            
            const foundVersion = freshAppData.versions.find((v: AppVersion) => v.id === newVer.id) || freshAppData.versions[0];
            const finalScreens = foundVersion?.screens || [];
            
            set({ 
              apps: updatedApps, 
              currentApp: updatedApps[appIndex],
              currentVersion: foundVersion,
              currentVersionId: foundVersion.id,
              currentScreen: finalScreens.length > 0 ? finalScreens[0] : null,
              currentScreenId: finalScreens.length > 0 ? finalScreens[0].id : null
            });
            
            // Persist to localStorage using centralized utility
            saveVersionId(foundVersion.id);
            if (finalScreens.length > 0 && finalScreens[0].id) {
              saveScreenId(finalScreens[0].id);
            }
            
            return foundVersion.id;
          }
        }
      } catch (error) {
        // Прокидываем ошибку дальше чтобы UI мог её обработать
        console.log('[addVersion] ❌ Firebase error:', error);
        console.log('[addVersion] ❌ Error message:', error?.message);
        console.log('[addVersion] ❌ Error code:', (error as any)?.code);
        console.log('[addVersion] ❌ Error details:', JSON.stringify(error, null, 2));
        
        // Rollback on Firebase error
        set({
          apps: previousApps,
          currentApp: previousCurrentApp,
          currentVersion: previousCurrentVersion,
          currentVersionId: previousCurrentVersion?.id || null,
          currentScreen: previousCurrentScreen,
          currentScreenId: previousCurrentScreen?.id || null
        });
        
        // Прокидываем оригинальную ошибку
        throw error;
      }
    }
    
    // Если Firebase недоступен, создаём локальную версию как fallback
    if (!newVersion) {
      newVersion = {
        id: generateId(),
        version,
        name,
        releaseDate: new Date().toISOString(),
        isActive: true,
        screens: copiedScreens
      };
    }
    
    const updatedVersions = (currentApp.versions || []).map((v: AppVersion) => ({ ...v, isActive: false }));
    updatedVersions.unshift(newVersion);
    
    const updatedApps = [...apps];
    updatedApps[appIndex] = { ...updatedApps[appIndex], versions: updatedVersions, updatedAt: new Date().toISOString() };
    
    // Firebase функция вернёт screens уже скопированные (если copyScreens=true)
    // Используем screens из newVersion
    const finalScreens = newVersion?.screens || [];
    
    set({ 
      apps: updatedApps, 
      currentApp: updatedApps[appIndex],
      currentVersion: newVersion,
      currentVersionId: newVersion.id,
      currentScreen: finalScreens.length > 0 ? finalScreens[0] : null,
      currentScreenId: finalScreens.length > 0 ? finalScreens[0].id : null
    });
    
    // Persist to localStorage using centralized utility
    saveVersionId(newVersion.id);
    if (finalScreens.length > 0 && finalScreens[0].id) {
      saveScreenId(finalScreens[0].id);
    }
    
    return newVersion.id;
  },
  
  // Update version
  updateVersion: async (versionId: string, updates: Partial<Pick<AppVersion, 'version' | 'name'>>) => {
    const { apps, currentApp, currentVersion, user } = get() as any;
    if (!currentApp) return;
    
    // Find old version for history
    const oldVersion = currentApp.versions.find((v: AppVersion) => v.id === versionId);
    
    const updatedVersions = currentApp.versions.map((v: AppVersion) => {
      if (v.id === versionId) {
        return { ...v, ...updates };
      }
      return v;
    });
    
    const updatedApps = apps.map((app: App) => {
      if (app.id === currentApp.id) {
        return {
          ...app,
          versions: updatedVersions,
          updatedAt: new Date().toISOString()
        };
      }
      return app;
    });
    
    let updatedCurrentVersion = currentVersion;
    if (currentVersion?.id === versionId) {
      updatedCurrentVersion = { ...currentVersion, ...updates };
    }
    
    set({ 
      apps: updatedApps, 
      currentApp: { ...currentApp, versions: updatedVersions },
      currentVersion: updatedCurrentVersion
    });
    
    // Sync to Firebase if available
    if (isFirebaseAvailable() && user && currentApp) {
      try {
        await firebaseUpdateVersion(
          currentApp.id,
          versionId,
          updates,
          user,
          oldVersion
        );
        
        // Update timestamp AFTER successful Firebase operation
        const { updateLastLocalUpdate } = (get() as any);
        if (updateLastLocalUpdate) updateLastLocalUpdate();
        
        console.log('[updateVersion] Saved to Firebase:', { versionId, updates });
      } catch (error) {
        console.error('[updateVersion] Error saving to Firebase:', error);
      }
    }
  },
  
  // Delete version
  deleteVersion: async (versionId: string) => {
    const { apps, currentApp, currentVersion, user } = get() as any;
    if (!currentApp) return;
    
    if (currentApp.versions.length <= 1) return;
    
    // Delete from Firebase first
    if (isFirebaseAvailable() && user) {
      const success = await firebaseDeleteVersion(currentApp.id, versionId, user);
      
      // Update timestamp AFTER successful Firebase operation
      const { updateLastLocalUpdate } = (get() as any);
      if (updateLastLocalUpdate) updateLastLocalUpdate();
      
      if (!success) {
        console.error('[deleteVersion] Failed to delete version from Firebase');
        return;
      }
    }
    
    const updatedVersions = currentApp.versions.filter((v: AppVersion) => v.id !== versionId);
    
    if (updatedVersions.length > 0 && !updatedVersions.some((v: AppVersion) => v.isActive)) {
      updatedVersions[0].isActive = true;
    }
    
    const updatedApps = apps.map((app: App) => {
      if (app.id === currentApp.id) {
        return {
          ...app,
          versions: updatedVersions,
          updatedAt: new Date().toISOString()
        };
      }
      return app;
    });
    
    const newCurrentVersion = currentVersion?.id === versionId 
      ? (updatedVersions.length > 0 ? updatedVersions[0] : null)
      : currentVersion;
    
    set({ 
      apps: updatedApps, 
      currentApp: { ...currentApp, versions: updatedVersions },
      currentVersion: newCurrentVersion,
      currentVersionId: newCurrentVersion?.id || null,
      currentScreen: newCurrentVersion?.screens[0] || null,
      currentScreenId: newCurrentVersion?.screens[0]?.id || null
    });
  },
  
  // Set current screen
  setCurrentScreen: (screenId: string) => {
    const { currentVersion } = get() as any;
    if (!currentVersion) return;
    
    const screen = currentVersion.screens.find((s: Screen) => s.id === screenId);
    if (screen) {
      set({ 
        currentScreen: screen, 
        currentScreenId: screen.id
      });
      // Persist to localStorage using centralized utility
      saveScreenId(screen.id);
    } else {
      set({ 
        currentScreen: null, 
        currentScreenId: null
      });
      localStorage.removeItem('currentScreenId');
    }
  },
  
  // Add new screen
  addScreen: async (screenData: { name: string; displayName: string; imageUrl?: string }) => {
    const { apps, currentApp, currentVersion, user } = get() as any;
    if (!currentApp || !currentVersion) return null;
    
    // Сохраняем предыдущее состояние для rollback
    const previousApps = apps;
    const previousCurrentApp = currentApp;
    const previousCurrentVersion = currentVersion;
    const previousCurrentScreen = get().currentScreen;
    
    if (isFirebaseAvailable() && user) {
      // Создаем экран в Firebase и получаем результат с правильным ID
      // Передаем currentVersion как fallback для решения race condition
      const result = await firebaseCreateScreen(currentApp.id, currentVersion.id, screenData, user, currentVersion);
      
      // Если Firebase вернул null - отменяем операцию
      if (!result || !result.data) {
        console.error('[addScreen] Failed to create screen in Firebase:', result);
        return null;
      }
      
      // Update timestamp AFTER successful Firebase operation
      const { updateLastLocalUpdate } = (get() as any);
      if (updateLastLocalUpdate) updateLastLocalUpdate();
      
      // Используем данные экрана из Firebase
      const newScreen: Screen = result.data;
      
      const updatedVersions = currentApp.versions.map((v: AppVersion) => {
        if (v.id === currentVersion.id) {
          return { ...v, screens: [...v.screens, newScreen] };
        }
        return v;
      });
      
      const updatedApps = apps.map((app: App) => {
        if (app.id === currentApp.id) {
          return {
            ...app,
            versions: updatedVersions,
            updatedAt: new Date().toISOString()
          };
        }
        return app;
      });
      
      const updatedCurrentVersion = updatedVersions.find((v: AppVersion) => v.id === currentVersion.id) || currentVersion;
      
      set({
        apps: updatedApps,
        currentApp: { ...currentApp, versions: updatedVersions },
        currentVersion: updatedCurrentVersion,
        currentScreen: newScreen,
        currentScreenId: newScreen.id
      });
      
      // Persist to localStorage using centralized utility
      saveScreenId(newScreen.id);
      
      return newScreen.id;
    }
    
    const newScreen: Screen = {
      id: generateId(),
      name: screenData.name,
      displayName: screenData.displayName,
      imageUrl: screenData.imageUrl || '',
      events: []
    };
    
    const updatedVersions = currentApp.versions.map((v: AppVersion) => {
      if (v.id === currentVersion.id) {
        return { ...v, screens: [...v.screens, newScreen] };
      }
      return v;
    });
    
    const updatedApps = apps.map((app: App) => {
      if (app.id === currentApp.id) {
        return {
          ...app,
          versions: updatedVersions,
          updatedAt: new Date().toISOString()
        };
      }
      return app;
    });
    
    const updatedCurrentVersion = updatedVersions.find((v: AppVersion) => v.id === currentVersion.id) || currentVersion;
    
    set({ 
      apps: updatedApps, 
      currentApp: { ...currentApp, versions: updatedVersions },
      currentVersion: updatedCurrentVersion,
      currentScreen: newScreen,
      currentScreenId: newScreen.id
    });
    
    // Persist to localStorage using centralized utility
    saveScreenId(newScreen.id);
    
    return newScreen.id;
  },
  
  // Upload screen image
  uploadScreenImage: async (file: File): Promise<string | null> => {
    const { currentApp } = get() as any;
    if (!currentApp) return null;
    
    // Only use Cloudinary (Firebase Storage is disabled for security)
    if (isCloudinaryAvailable()) {
      console.log('[uploadScreenImage] Using Cloudinary for upload');
      const url = await uploadToCloudinary(file, currentApp.id);
      if (url) return url;
    }
    
    // Fallback: Local blob URL (temporary, lost on refresh)
    console.log('[uploadScreenImage] Using local blob URL as fallback');
    return URL.createObjectURL(file);
  },
  
  // Update screen
  updateScreen: async (screenId: string, updates: Partial<Pick<Screen, 'name' | 'displayName' | 'imageUrl'>>) => {
    const { apps, currentApp, currentVersion, currentScreen, user } = get() as any;
    if (!currentApp || !currentVersion) return;
    
    // Find the old screen for Firebase update
    const oldScreen = currentVersion.screens.find((s: Screen) => s.id === screenId);
    
    const updatedVersions = currentApp.versions.map((v: AppVersion) => {
      if (v.id === currentVersion.id) {
        return {
          ...v,
          screens: v.screens.map((s: Screen) => {
            if (s.id === screenId) {
              return { ...s, ...updates };
            }
            return s;
          })
        };
      }
      return v;
    });
    
    const updatedApps = apps.map((app: App) => {
      if (app.id === currentApp.id) {
        return {
          ...app,
          versions: updatedVersions,
          updatedAt: new Date().toISOString()
        };
      }
      return app;
    });
    
    let updatedCurrentScreen = currentScreen;
    if (currentScreen?.id === screenId) {
      updatedCurrentScreen = { ...currentScreen, ...updates };
    }
    
    // Создаём новый объект currentVersion для гарантии реактивности Zustand
    const updatedScreens = currentVersion.screens.map((s: Screen) => {
      if (s.id === screenId) {
        return { ...s, ...updates };
      }
      return s;
    });
    
    const updatedCurrentVersion = {
      ...currentVersion,
      screens: updatedScreens
    };
    
    set({
      apps: updatedApps,
      currentApp: { ...currentApp, versions: updatedVersions },
      currentVersion: updatedCurrentVersion,
      currentScreen: updatedCurrentScreen
    });
    
    // Save to Firebase if available
    if (isFirebaseAvailable() && user && oldScreen) {
      try {
        await firebaseUpdateScreen(
          currentApp.id,
          currentVersion.id,
          screenId,
          updates,
          user,
          oldScreen
        );
        
        // Update timestamp AFTER successful Firebase operation
        const { updateLastLocalUpdate } = (get() as any);
        if (updateLastLocalUpdate) updateLastLocalUpdate();
        
        console.log('[updateScreen] Saved to Firebase:', { screenId, updates });
      } catch (error) {
        console.error('[updateScreen] Error saving to Firebase:', error);
      }
    }
  },
  
  // Delete screen
  deleteScreen: async (screenId: string) => {
    const { apps, currentApp, currentVersion, currentScreen, user } = get() as any;
    if (!currentApp || !currentVersion) return;
    
    // Delete from Firebase first
    if (isFirebaseAvailable() && user) {
      const success = await firebaseDeleteScreen(currentApp.id, currentVersion.id, screenId, user);
      
      // Update timestamp AFTER successful Firebase operation
      const { updateLastLocalUpdate } = (get() as any);
      if (updateLastLocalUpdate) updateLastLocalUpdate();
      
      if (!success) {
        console.error('[deleteScreen] Failed to delete screen from Firebase');
        return;
      }
    }
    
    const updatedVersions = currentApp.versions.map((v: AppVersion) => {
      if (v.id === currentVersion.id) {
        return {
          ...v,
          screens: v.screens.filter((s: Screen) => s.id !== screenId)
        };
      }
      return v;
    });
    
    const updatedApps = apps.map((app: App) => {
      if (app.id === currentApp.id) {
        return {
          ...app,
          versions: updatedVersions,
          updatedAt: new Date().toISOString()
        };
      }
      return app;
    });
    
    const remainingScreens = currentVersion.screens.filter((s: Screen) => s.id !== screenId);
    const newCurrentScreen = currentScreen?.id === screenId 
      ? (remainingScreens.length > 0 ? remainingScreens[0] : null)
      : currentScreen;
    
    const updatedCurrentVersion = updatedVersions.find((v: AppVersion) => v.id === currentVersion.id) || currentVersion;
    
    set({ 
      apps: updatedApps, 
      currentApp: { ...currentApp, versions: updatedVersions },
      currentVersion: updatedCurrentVersion,
      currentScreen: newCurrentScreen,
      currentScreenId: newCurrentScreen?.id || null
    });
  },
  
  // Reorder screens
  reorderScreens: (screenIds: string[]) => {
    const { apps, currentApp, currentVersion, currentScreen } = get() as any;
    if (!currentApp || !currentVersion) return;
    
    const currentScreens = currentVersion.screens;
    const reorderedScreens = screenIds
      .map(id => currentScreens.find((s: Screen) => s.id === id))
      .filter((s): s is Screen => s !== undefined);
    
    const updatedVersions = currentApp.versions.map((v: AppVersion) => {
      if (v.id === currentVersion.id) {
        return { ...v, screens: reorderedScreens };
      }
      return v;
    });
    
    const updatedApps = apps.map((app: App) => {
      if (app.id === currentApp.id) {
        return {
          ...app,
          versions: updatedVersions,
          updatedAt: new Date().toISOString()
        };
      }
      return app;
    });
    
    set({ 
      apps: updatedApps, 
      currentApp: { ...currentApp, versions: updatedVersions },
      currentVersion: { ...currentVersion, screens: reorderedScreens },
      currentScreen: reorderedScreens.find((s: Screen) => s.id === currentScreen?.id) || null
    });
  },
  
  // Move screen up or down
  moveScreen: (screenId: string, direction: 'up' | 'down') => {
    const { currentVersion } = get() as any;
    if (!currentVersion) return;
    
    const screens = currentVersion.screens;
    const currentIndex = screens.findIndex((s: Screen) => s.id === screenId);
    
    if (currentIndex === -1) return;
    
    if (direction === 'up' && currentIndex === 0) return;
    if (direction === 'down' && currentIndex === screens.length - 1) return;
    
    const newOrder = screens.map((s: Screen) => s.id);
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    [newOrder[currentIndex], newOrder[targetIndex]] = [newOrder[targetIndex], newOrder[currentIndex]];
    
    get().reorderScreens(newOrder);
  },
  
  // Update screen marker scale
  updateScreenMarkerScale: async (screenId: string, scale: number) => {
    const { apps, currentApp, currentVersion, currentScreen, user } = get() as any;
    if (!currentApp || !currentVersion) return;
    
    const clampedScale = Math.max(0.1, Math.min(2, scale));
    
    // Find the old screen for history logging
    const oldScreen = currentVersion.screens.find((s: Screen) => s.id === screenId);
    
    const updatedVersions = currentApp.versions.map((v: AppVersion) => {
      if (v.id === currentVersion.id) {
        return {
          ...v,
          screens: v.screens.map((s: Screen) => {
            if (s.id === screenId) {
              return { ...s, markerScale: clampedScale };
            }
            return s;
          })
        };
      }
      return v;
    });
    
    const updatedApps = apps.map((app: App) => {
      if (app.id === currentApp.id) {
        return {
          ...app,
          versions: updatedVersions,
          updatedAt: new Date().toISOString()
        };
      }
      return app;
    });
    
    let updatedCurrentScreen = currentScreen;
    if (currentScreen?.id === screenId) {
      updatedCurrentScreen = { ...currentScreen, markerScale: clampedScale };
    }
    
    const updatedCurrentVersion = updatedVersions.find((v: AppVersion) => v.id === currentVersion.id) || currentVersion;
    
    set({ 
      apps: updatedApps, 
      currentApp: { ...currentApp, versions: updatedVersions },
      currentVersion: updatedCurrentVersion,
      currentScreen: updatedCurrentScreen
    });
    
    // Save to Firebase if available
    if (isFirebaseAvailable() && user && oldScreen) {
      try {
        await firebaseUpdateScreen(
          currentApp.id,
          currentVersion.id,
          screenId,
          { markerScale: clampedScale },
          user,
          oldScreen
        );
        
        // Update timestamp AFTER successful Firebase operation
        const { updateLastLocalUpdate } = (get() as any);
        if (updateLastLocalUpdate) updateLastLocalUpdate();
        
        console.log('[updateScreenMarkerScale] Saved to Firebase:', { screenId, markerScale: clampedScale });
      } catch (error) {
        console.error('[updateScreenMarkerScale] Error saving to Firebase:', error);
      }
    }
  },
  
  // Update screen popup scale
  updateScreenPopupScale: async (screenId: string, scale: number) => {
    const { apps, currentApp, currentVersion, currentScreen, user } = get() as any;
    if (!currentApp || !currentVersion) return;
    
    const clampedScale = Math.max(0.1, Math.min(2, scale));
    
    // Find the old screen for history logging
    const oldScreen = currentVersion.screens.find((s: Screen) => s.id === screenId);
    
    const updatedVersions = currentApp.versions.map((v: AppVersion) => {
      if (v.id === currentVersion.id) {
        return {
          ...v,
          screens: v.screens.map((s: Screen) => {
            if (s.id === screenId) {
              return { ...s, popupScale: clampedScale };
            }
            return s;
          })
        };
      }
      return v;
    });
    
    const updatedApps = apps.map((app: App) => {
      if (app.id === currentApp.id) {
        return {
          ...app,
          versions: updatedVersions,
          updatedAt: new Date().toISOString()
        };
      }
      return app;
    });
    
    let updatedCurrentScreen = currentScreen;
    if (currentScreen?.id === screenId) {
      updatedCurrentScreen = { ...currentScreen, popupScale: clampedScale };
    }
    
    const updatedCurrentVersion = updatedVersions.find((v: AppVersion) => v.id === currentVersion.id) || currentVersion;
    
    set({ 
      apps: updatedApps, 
      currentApp: { ...currentApp, versions: updatedVersions },
      currentVersion: updatedCurrentVersion,
      currentScreen: updatedCurrentScreen
    });
    
    // Save to Firebase if available
    if (isFirebaseAvailable() && user && oldScreen) {
      try {
        await firebaseUpdateScreen(
          currentApp.id,
          currentVersion.id,
          screenId,
          { popupScale: clampedScale },
          user,
          oldScreen
        );
        
        // Update timestamp AFTER successful Firebase operation
        const { updateLastLocalUpdate } = (get() as any);
        if (updateLastLocalUpdate) updateLastLocalUpdate();
        
        console.log('[updateScreenPopupScale] Saved to Firebase:', { screenId, popupScale: clampedScale });
      } catch (error) {
        console.error('[updateScreenPopupScale] Error saving to Firebase:', error);
      }
    }
  }
});
